// The pricing matrix table. Sticky header; sticky category bands inside the
// body separate sections. Renders a flat row list pre-filtered by the page;
// ordering and grouping are the builder's job (see utils/pricing-matrix.js).
//
// Columns depend on the section's pricing strategy: tiered shows the four
// canonical area-bucket rate columns; flat shows a single "Flat price" column.
import {
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
} from '@mui/material';
import { Fragment } from 'react';
import PricingCategoryRow from './PricingCategoryRow.jsx';
import PricingTargetRow from './PricingTargetRow.jsx';
import { AREA_BUCKETS, bucketLabel } from '../../../config/pricing-buckets.js';
import { PRICING_STRATEGIES } from '../../../config/pricing-strategies.js';

const PricingMatrixTable = ({ rows, strategy = PRICING_STRATEGIES.TIERED }) => {
  const isFlat = strategy === PRICING_STRATEGIES.FLAT;
  const isCustomQuote = strategy === PRICING_STRATEGIES.CUSTOM_QUOTE;
  // Value column headers vary by strategy; label column is always present.
  let valueColumns;
  if (isCustomQuote) {
    valueColumns = [{ key: 'custom', label: 'Pricing' }];
  } else if (isFlat) {
    valueColumns = [{ key: 'flat', label: 'Flat price' }];
  } else {
    valueColumns = AREA_BUCKETS.map((bucket) => ({ key: bucket.min, label: bucketLabel(bucket) }));
  }
  const columnCount = 1 + valueColumns.length;

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ width: '100%', maxHeight: 'calc(100vh - 260px)' }}
    >
      <Table stickyHeader size="small" aria-label="Pricing matrix">
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 280, fontWeight: 700 }}>Product / Option</TableCell>
            {valueColumns.map((col) => (
              <TableCell key={col.key} align="right" sx={{ fontWeight: 700 }}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => {
            const prev = rows[idx - 1];
            const showCategoryBand = !prev || prev.categoryId !== row.categoryId;
            return (
              <Fragment key={row.key}>
                {showCategoryBand && (
                  <PricingCategoryRow name={row.categoryName} columnCount={columnCount} />
                )}
                <PricingTargetRow row={row} strategy={strategy} />
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PricingMatrixTable;
